import { breakpoints } from "@gigpro/ui/utils/useBreakpoint";
import type { LinkOptions, ParsedLocation, StaticDataRouteOption } from "@tanstack/react-router";

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    messagingDepth?: number;
    messagingListDepth?: number;
  }
}

export type MatchRoutes = (location: ParsedLocation) => Array<{ staticData: StaticDataRouteOption }>;
type ViewTransitionOptions = Exclude<LinkOptions["viewTransition"], boolean | undefined>;

let skipNext = false;
// Swipe-back gestures already animate natively (iOS Safari, Android Chrome); don't stack ours on top.
globalThis.addEventListener?.("popstate", (event) => {
  skipNext = "hasUAVisualTransition" in event && event.hasUAVisualTransition === true;
});

// On desktop the chat opens beside the list, so only list levels count.
const getDepth = (matchRoutes: MatchRoutes, location: ParsedLocation) => {
  const isDesktop = globalThis.matchMedia(`(min-width: ${breakpoints.md}px)`).matches;
  const { staticData } = matchRoutes(location).at(-1) ?? {};
  return isDesktop ? staticData?.messagingListDepth : staticData?.messagingDepth;
};

// Without view-transition types the router would crossfade every navigation app-wide.
export const messagingViewTransition = (matchRoutes: MatchRoutes): ViewTransitionOptions | undefined =>
  globalThis.CSS?.supports?.("selector(:active-view-transition-type(a))")
    ? {
        types: ({ fromLocation, toLocation }) => {
          const skip = skipNext;
          skipNext = false;
          if (skip || !fromLocation) return false;
          const from = getDepth(matchRoutes, fromLocation);
          const to = getDepth(matchRoutes, toLocation);
          if (from === undefined || to === undefined || from === to) return false;
          return [to > from ? "messaging-forward" : "messaging-back"];
        },
      }
    : undefined;
